import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { API_ENDPOINT, ERROR_MESSAGE, SESSION_STORAGE_KEY, LOCAL_STORAGE_KEY, CONTENT_LOG, SAUCER_LOG_ACTION, getApiPath } from '@core/configs';
import { Mode, ROUTE_PATH, TV } from '@core/constants';
import { BehaviorSubject, EMPTY, lastValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { 
  LocalStorageService,
  RequestService, 
  HttpCancelService,
  SessionStorageService,
  ApiService,
  GlobalDataService,
  FullscreenService,
  SessionContextService,
  TvLoginSessionService
} from '@core/services/common';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ApiResponse, UserSession } from '@core/models/interfaces';
import { FirebaseDatabaseService } from '../common/firebase/firebase-database.service';
import { FirebaseAuthService } from '../common/firebase/firebase-auth.service';
import { AvatarDealerService } from '../common/avatar-dealer.service';
import { DeviceLicensesService } from './device-licenses.service';
import { SaucerLogModel } from '@core/models/models';
import { SaucerLogService } from '../saucer-logs/saucer-log.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private isLoggedIn = new BehaviorSubject<boolean>(false);
  private isLoggingOut = false; // Flag để ngăn race condition khi logout.
  private jwtHelperService: JwtHelperService = new JwtHelperService();
  private firebaseDatabaseService: FirebaseDatabaseService | undefined;
  private firebaseAuthService: FirebaseAuthService | undefined;
  constructor(
    private readonly http: ApiService,
    private localService: LocalStorageService,
    private sessionStorageService : SessionStorageService,
    private globalDataService: GlobalDataService,
    private sessionContextService: SessionContextService,
    private tvLoginSessionService: TvLoginSessionService,
    private requestService: RequestService,
    private cancelService: HttpCancelService,
    private injector: Injector,
    private router: Router,
    private fullScreenService: FullscreenService,
    private saucerLogService: SaucerLogService
  ) {
    this.tvLoginSessionService.restoreSession();
    if (this.loggedIn()) {
      this.setIsLoggedIn(true);
    }
  }

  get isAccessTokenExpired(): boolean {
    const accessToken = this.sessionStorageService.getAccessToken();
    if (accessToken) {
      return this.jwtHelperService.isTokenExpired(accessToken);
    }
    return true;
  }

  get isRefreshTokenExpired(): boolean {
    const refreshToken = this.sessionStorageService.getRefreshToken();
    if (refreshToken) {
      return this.jwtHelperService.isTokenExpired(refreshToken);
    }
    return true;
  }

  setIsLoggedIn(value: boolean) {
    this.isLoggedIn.next(value);
  }

  getIsLoggedIn() {
    return this.isLoggedIn.asObservable();
  }

  async logout() {
    const mode = this.localService.getItem(LOCAL_STORAGE_KEY.mode);
    const corpPrefix = this.sessionContextService.getCorpPrefix();
    const officeSelected = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.officeSelected);
    const deviceToken = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.deviceToken);
    const shouldCallLogout = !this.isAccessTokenExpired || !this.isRefreshTokenExpired;
    this.tvLoginSessionService.clear();

    if (!this.requestService.checkCompletedRequests()) {
      this.cancelService.cancelPendingRequest();
    }

    await this.logoutFirebase(corpPrefix, officeSelected, deviceToken);

    if (shouldCallLogout) {
      try {
        await lastValueFrom(this.callLogout());
      } catch {
        this.callLogout();
        this.saucerLogService.error(
          {
            apiPath: getApiPath(API_ENDPOINT.token.logout),
          },
          {
            action: SAUCER_LOG_ACTION.LOGOUT.FAILED
          }
        );
      }
    }

    const urlCurrent = this.router.url;
    const log: SaucerLogModel = {
      apiPath: getApiPath(API_ENDPOINT.token.logout),
      content: CONTENT_LOG.LOGOUT + ": " + urlCurrent,
    };
    await this.saucerLogService.system(
      log,
      {
        action: SAUCER_LOG_ACTION.LOGOUT.SUCCESS
      }
    );
    this.saucerLogService.sendLogImmediately();

    // Clear session BEFORE navigating so LoginComponent.keepStaffLogin() won't see user as logged in
    this.sessionStorageService.clearSession();
    this.globalDataService.clear();
    // Wipe the avatar dealer cache on logout so the next login re-fetches every
    // avatar from scratch, instead of relying on per-image self-heal. Self-heal
    // stays in place for runtime broken-URL recovery within a session.
    this.injector.get(AvatarDealerService).clear();
    this.setIsLoggedIn(false);
    
    this.localService.clear([
      LOCAL_STORAGE_KEY.settingDialog,
      LOCAL_STORAGE_KEY.corpPrefix,
      LOCAL_STORAGE_KEY.enCorpPrefix,
      LOCAL_STORAGE_KEY.mode,
      LOCAL_STORAGE_KEY.custSession,
      LOCAL_STORAGE_KEY.tvDeviceNameRegistered
    ]);

    // Đặt flag TRƯỚC KHI navigate để ngăn keepStaffLogin() chạy.
    this.isLoggingOut = true; 
    if (corpPrefix) {
      if (mode == Mode.TV) {
        (window as any).AndroidBridge.onWebLogOut();
      }
      const navigated = await this.router.navigateByUrl(`${ROUTE_PATH.LOGIN}`, { replaceUrl: true });
      if (!navigated) {
        this.isLoggingOut = false; // Reset flag nếu navigate thất bại
        return;
      }
    }

    this.fullScreenService.isLockPageSubject.next("false");
    this.isLoggingOut = false; // Reset flag sau khi hoàn thành.
  }

  async logoutFirebase(corpPrefix: string, officeSelected: any, deviceToken?: string) {
    // A device token means a Realtime Database session may exist. Avoid creating
    // FirebaseDatabaseService solely to clean up a partially completed login.
    if (deviceToken) {
      this.firebaseDatabaseService = this.injector.get(FirebaseDatabaseService);
      await this.firebaseDatabaseService.logoutFirebaseRealtimeDB(deviceToken);
      this.firebaseDatabaseService.hasConnectedBefore=false;
    }
    // Firebase Auth can remain signed in even when office context was cleared.
    this.firebaseAuthService = this.injector.get(FirebaseAuthService);
    await this.firebaseAuthService.logoutWithFirebaseToken();
  }

  callLogout() {
    return this.http.delete(API_ENDPOINT.token.logout);
  }
  
  loggedIn() {
    // Nếu đang trong quá trình logout, return false ngay để tránh race condition.
    if (this.isLoggingOut) { 
      return false; 
    } 

    if (this.isRefreshTokenExpired) {
      if (this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.loginSession)) {
        this.tvLoginSessionService.clear();
        this.logout();
      }
      return false;
    } else {
      return true;
    }
  }

  checkCorpPrefix(corpPrefix: string) {
    const url = `${API_ENDPOINT.auth.checkExistsCorpPrefix}/${corpPrefix}`;
    return this.http.get<any>(url).pipe(
      catchError((error: HttpErrorResponse) => {
        const message =
          error.status === 404 ? 'ページが見つかりません' : 'サービスが利用できません';
        return of({ result: null, status: error.status, message } as ApiResponse<any>);
      }),
    );
  }

  async checkStaffCode(): Promise<boolean> {
     // TODO: Implement API call to Caremoni service to verify staff code
    return true;
  }

  requestAccessToken(refreshToken: string): Observable<ApiResponse<any>> {
    if (refreshToken) {
      return this.http.post(API_ENDPOINT.token.refreshToken, {
        refreshToken,
      });
    } else {
      return throwError(ERROR_MESSAGE.refreshTokenNotFound);
    }
  }

  /** Applies every successful token refresh and keeps the TV device record current. */
  async applyRefreshedSession(session: UserSession): Promise<void> {
    this.sessionStorageService.setUserSession(session);
    await this.injector.get(DeviceLicensesService).updateLoginCustomerInfoByDeviceId(session.accessToken);
  }

  async getInfoCkapi() {
    const url = `${API_ENDPOINT.token.verifyAndGetCkapi}`;
    const user = this.sessionStorageService.getUserSession();
    const office = this.sessionContextService.getOfficeSelected<any>();
    const body = { 
        "accessToken": user?.accessToken, 
        "companyId": office.companyId, 
        "module":"CAREMONI" 
    }
    return await lastValueFrom(this.http.post(url, body));
  }
}
